import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, X, Globe, MessageSquare } from 'lucide-react';
import { PRODUCT_IMAGES } from '../data/images';
import { generateGeneralWhatsAppLink } from '../data/products';

const LookbookGallery = () => {
  const [activeModalImage, setActiveModalImage] = useState(null);

  const lookbookItems = [
    {
      src: PRODUCT_IMAGES[11],
      title: "IMPERIAL INDIGO SLIM FIT",
      color: "Rinsin blue",
      fit: "Bootcut fit",
      span: "span 2"
    },
    {
      src: PRODUCT_IMAGES[7] || '/denim_category_jackets.png',
      title: "MONARCH UTILITY JACKET",
      color: "Dark indigo",
      fit: "Relaxed fit",
      span: "span 1"
    },
    {
      src: PRODUCT_IMAGES[8] || '/denim_category_selvage.png',
      title: "MIDNIGHT RED-LINE SELVAGE",
      color: "Blue selvedge",
      fit: "Straight fit",
      span: "span 1"
    },
    {
      src: PRODUCT_IMAGES[4],
      title: "ONYX CHARCOAL TAPERED",
      color: "Dk black",
      fit: "Slim tapper",
      span: "span 1"
    },
    {
      src: PRODUCT_IMAGES[6],
      title: "ARTISAN VINTAGE STONE-WASH",
      color: "Vintage blue",
      fit: "Straight ankle",
      span: "span 2"
    }
  ];

  return (
    <section style={{ padding: '90px 0', background: '#f8fafc', borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
      <div className="container">
        
        {/* Section Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px', marginBottom: '40px' }}>
          <div>
            <span className="section-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={13} />
              EXPORT EDITORIAL LOOKBOOK
            </span>
            <h2 className="section-title" style={{ marginBottom: 0, fontSize: 'clamp(28px, 4.2vw, 46px)' }}>
              EDITORIAL <span className="gradient-text">CAMPAIGN</span>
            </h2>
          </div>

          <p style={{ color: 'var(--text-muted)', fontSize: '14px', maxWidth: '420px', margin: 0, lineHeight: 1.5 }}>
            High-definition production highlights engineered for international retail chains, brand partners, and global import catalogs.
          </p>
        </div>

        {/* Gallery Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}
        >
          {lookbookItems.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              whileHover={{ y: -6 }}
              onClick={() => setActiveModalImage(item)}
              style={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                aspectRatio: '3/4',
                boxShadow: '0 12px 36px rgba(15, 23, 42, 0.08)',
                cursor: 'pointer',
                background: '#e2e8f0'
              }}
            >
              <img
                src={item.src}
                alt={item.title}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  display: 'block',
                  transition: 'transform 0.5s ease'
                }}
              />

              {/* Gradient Overlay */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(15, 23, 42, 0.85) 0%, transparent 60%)',
                  opacity: 0.9,
                  transition: 'opacity 0.3s ease'
                }}
              />

              {/* Top Zoom Icon Badge */}
              <div
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0f172a',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)'
                }}
              >
                <Maximize2 size={15} />
              </div>

              {/* Bottom Information */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px', color: '#ffffff' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '4px',
                    background: 'rgba(224, 0, 135, 0.9)',
                    fontSize: '10px',
                    fontWeight: 900,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    marginBottom: '8px'
                  }}
                >
                  Color: {item.color} <span style={{ margin: '0 4px', opacity: 0.7 }}>|</span> Fit: {item.fit}
                </span>
                <h4
                  style={{
                    fontSize: '16px',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 900,
                    margin: 0,
                    letterSpacing: '0.02em',
                    color: '#ffffff'
                  }}
                >
                  {item.title}
                </h4>
              </div>
            </motion.div>
          ))}
        </div>

      </div>

      {/* Lightbox Image Preview Modal */}
      <AnimatePresence>
        {activeModalImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveModalImage(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px'
            }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative',
                maxWidth: '850px',
                width: '100%',
                maxHeight: '90vh',
                borderRadius: '16px',
                overflow: 'hidden',
                background: '#0f172a',
                boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <button
                onClick={() => setActiveModalImage(null)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  zIndex: 10,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>

              <div style={{ flex: 1, overflow: 'hidden', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={activeModalImage.src}
                  alt={activeModalImage.title}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
                />
              </div>

              <div style={{ padding: '24px', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontFamily: 'var(--font-heading)', fontWeight: 900, color: '#ffffff', margin: 0 }}>
                    {activeModalImage.title}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', margin: '4px 0 0' }}>
                    {activeModalImage.tag} • <span className="brand-name">RAYYAN DENIM</span> Global Export Edition
                  </p>
                </div>

                <a
                  href={generateGeneralWhatsAppLink(activeModalImage.title)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ padding: '12px 20px', fontSize: '12px' }}
                >
                  <MessageSquare size={16} />
                  ENQUIRE ABOUT THIS STYLE
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default LookbookGallery;
